import classNames from 'classnames'
import React, { ButtonHTMLAttributes } from 'react'

import LoadingAnimation from './icons/LoadingAnimation'

export interface ButtonProps
  extends Omit<
    ButtonHTMLAttributes<HTMLButtonElement>,
    'onClick' | 'onKeyDown'
  > {
  className?: string
  isLoading?: boolean
  variant?: 'solid' | 'plain' | 'outline' | 'link' | 'danger' | 'danger-outline'
  size?: 'base' | 'lg' | 'xl'
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type']
  onClick?(event: React.MouseEvent<HTMLButtonElement>): unknown
  onKeyDown?(event: React.KeyboardEvent<HTMLButtonElement>): unknown
}

export const buttonClasses = ({
  size = 'base',
  variant = 'solid',
}: Pick<ButtonProps, 'size' | 'variant'> = {}) =>
  classNames(
    'inline-flex items-center justify-center gap-2',
    'cursor-pointer font-normal hyphens-none outline-hidden select-none',
    'disabled:cursor-not-allowed',
    // variants
    {
      'border-error bg-error text-white disabled:border-gray-500 disabled:bg-gray-500':
        variant === 'danger',
      'border-error text-error hover:bg-error-light2 focus:bg-error-light2 active:bg-error-light2 disabled:border-gray-500 disabled:text-gray-500 disabled:hover:bg-transparent':
        variant === 'danger-outline',
      'border-primary bg-primary text-secondary-light hover:bg-secondary-dark focus:bg-secondary-dark active:bg-secondary-dark disabled:border-gray-500 hover:border-secondary-dark focus:border-secondary-dark active:border-secondary-dark disabled:bg-gray-500':
        variant === 'solid',
      'border-primary text-primary hover:bg-primary-light2 focus:bg-primary-light2 active:bg-primary-light2 disabled:border-gray-500 disabled:text-gray-500 disabled:hover:bg-transparent':
        variant === 'outline',
      'rounded-none disabled:text-gray-500': variant === 'plain',
      'rounded-none text-secondary-dark disabled:text-gray-500':
        variant === 'link',
    },
    // styles affecting only non plain/link
    variant === 'plain' || variant === 'link'
      ? null
      : [
          'border transition-colors duration-200 ease-in-out',
          {
            'min-h-10 min-w-25 rounded-4xl px-8 text-base sm:min-h-12':
              size === 'lg',
            'min-h-12 min-w-55 rounded-4xl px-8 text-lg sm:min-h-15':
              size === 'xl',
            'min-h-8 min-w-25 rounded-sm px-3 text-sm sm:text-base':
              size === 'base',
          },
        ],
  )

const Button = ({
  className,
  children,
  id,
  isLoading,
  disabled,
  type = 'button',
  variant = 'solid',
  size = 'base',
  ...props
}: ButtonProps) => {
  return (
    <button
      className={classNames(buttonClasses({ size, variant }), className)}
      disabled={disabled || isLoading}
      type={type}
      {...props}
    >
      {isLoading ? <LoadingAnimation /> : children}
    </button>
  )
}

export default Button
